import { z } from "zod";
import { ItemSchema } from "./items";
import { HydratedCompanySchema } from "./companies";

// Enums
export const InvoiceStatusSchema = z.enum(["DRAFT", "FINALIZED"]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;
export const InvoiceStatusEnum = InvoiceStatusSchema.enum;

export const InvoiceLineTypeSchema = z.enum(["ITEM", "CUSTOM"]);
export type InvoiceLineType = z.infer<typeof InvoiceLineTypeSchema>;
export const InvoiceLineTypeEnum = InvoiceLineTypeSchema.enum;

// InvoiceLine Schema
export const InvoiceLineSchema = z.object({
  id: z.string(),
  itemId: z.string().nullable(),
  item: ItemSchema.nullable(),
  description: z.string().nullable(),
  quantity: z.string(),
  unitPrice: z.string(),
});

export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const InvoiceSchema = z.object({
  id: z.string(),
  status: InvoiceStatusSchema,
  invoiceNumber: z.number(),
  salesOrderId: z.string().nullable(),
  customerCompanyId: z.string().nullable(),
  customerCompany: HydratedCompanySchema.nullable().optional(),
  lines: z.array(InvoiceLineSchema),
  subtotal: z.string().nullable(),
  total: z.string().nullable(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoiceLineInputSchema = z.object({
  itemId: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.string(),
  unitPrice: z.string(),
});
export type InvoiceLineInput = z.infer<typeof InvoiceLineInputSchema>;

// API Request Schemas
export const CreateInvoiceParamsSchema = z.object({
  salesOrderId: z.string().optional(),
  customerCompanyId: z.string().optional(),
  lines: z.array(InvoiceLineInputSchema).optional(),
});
export type CreateInvoiceParams = z.infer<typeof CreateInvoiceParamsSchema>;

export const UpdateInvoiceParamsSchema = z.object({
  salesOrderId: z.string().optional(),
  customerCompanyId: z.string().optional(),
  lines: z.array(InvoiceLineInputSchema).optional(),
});
export type UpdateInvoiceParams = z.infer<typeof UpdateInvoiceParamsSchema>;

export const UpdateInvoiceStatusParamsSchema = z.object({
  status: InvoiceStatusSchema,
});
export type UpdateInvoiceStatusParams = z.infer<
  typeof UpdateInvoiceStatusParamsSchema
>;

// Filter for searching invoices
export const InvoiceFilterSchema = z.object({
  search: z.string().optional(),
  status: InvoiceStatusSchema.optional(),
  salesOrderId: z.string().optional(),
  ids: z.array(z.string()).optional(),
  invoiceNumbers: z.array(z.number()).optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export type InvoiceFilter = z.infer<typeof InvoiceFilterSchema>;

// API Response Schemas
export const CreateInvoiceResponseSchema = z.object({
  invoice: InvoiceSchema,
});
export type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;

export const GetInvoiceResponseSchema = z.object({
  invoice: InvoiceSchema,
});
export type GetInvoiceResponse = z.infer<typeof GetInvoiceResponseSchema>;

export const UpdateInvoiceResponseSchema = z.object({
  invoice: InvoiceSchema,
});
export type UpdateInvoiceResponse = z.infer<typeof UpdateInvoiceResponseSchema>;

export const ListInvoicesResponseSchema = z.object({
  invoices: z.array(InvoiceSchema),
});
export type ListInvoicesResponse = z.infer<typeof ListInvoicesResponseSchema>;

export const DeleteInvoiceResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteInvoiceResponse = z.infer<typeof DeleteInvoiceResponseSchema>;

// Error Response Schema
export const InvoiceErrorResponseSchema = z.object({
  error: z.string(),
});
export type InvoiceErrorResponse = z.infer<typeof InvoiceErrorResponseSchema>;

// Query Parameters Schema
export const ListInvoicesQuerySchema = z.object({
  status: InvoiceStatusSchema.optional(),
  salesOrderId: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});
export type ListInvoicesQuery = z.infer<typeof ListInvoicesQuerySchema>;
